import { db } from "@/firebase";
import { doc, getDoc } from "firebase/firestore";

const toNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const sum = (a, b) => a + b;

const formatLocalDate = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const isoLocalDate = () => formatLocalDate(new Date());

const getRecentDates = (days) => {
  const safeDays = Math.min(30, Math.max(1, Number(days) || 30));
  const base = new Date();
  base.setHours(12, 0, 0, 0);

  const dates = [];
  for (let i = safeDays - 1; i >= 0; i -= 1) {
    const d = new Date(base);
    d.setDate(base.getDate() - i);
    dates.push(formatLocalDate(d));
  }

  return dates;
};

const normalizeLevelsMap = (levels) => {
  if (!levels || typeof levels !== "object" || Array.isArray(levels)) {
    return {};
  }

  return levels;
};

const buildDailyStatDocId = (kidId, date) => `${kidId}_${date}`;

export default {
  namespaced: true,

  state: () => ({
    loading: false,
    error: "",
    userId: "",
    kidId: "",
    daily: [],
    levelsAgg: {},
    dailySeries: [],
    lastFetchAt: 0,
  }),

  mutations: {
    setLoading(state, value) {
      state.loading = !!value;
    },
    setError(state, value) {
      state.error = value || "";
    },
    setUser(state, uid) {
      state.userId = uid || "";
    },
    setKid(state, kidId) {
      state.kidId = kidId || "";
    },
    setDaily(state, arr) {
      state.daily = Array.isArray(arr) ? arr : [];
    },
    setLevelsAgg(state, map) {
      state.levelsAgg = map && typeof map === "object" ? map : {};
    },
    setDailySeries(state, arr) {
      state.dailySeries = Array.isArray(arr) ? arr : [];
    },
    setLastFetch(state, time) {
      state.lastFetchAt = time || 0;
    },
  },

  actions: {
    async fetch({ commit, rootState }, { userId = "", kidId = "", days = 30 } = {}) {
      const resolvedUserId =
        userId ||
        rootState?.auth?.user?.uid ||
        rootState?.auth?.loggedInUser?.uid ||
        "";

      const resolvedKidId =
        kidId ||
        rootState?.auth?.userData?.activeKidId ||
        rootState?.auth?.userData?.kidId ||
        "";

      if (!resolvedKidId) {
        commit("setError", "Missing kidId");
        commit("setUser", resolvedUserId);
        commit("setKid", "");
        commit("setDaily", []);
        commit("setDailySeries", []);
        commit("setLevelsAgg", {});
        return;
      }

      const safeDays = Math.min(30, Math.max(1, Number(days) || 30));
      const allowedDates = getRecentDates(safeDays);
      const allowedDateSet = new Set(allowedDates);

      try {
        commit("setLoading", true);
        commit("setError", "");
        commit("setUser", resolvedUserId);
        commit("setKid", resolvedKidId);

        const refs = allowedDates.map((date) =>
          doc(db, "dailyStats", buildDailyStatDocId(resolvedKidId, date))
        );

        const snaps = await Promise.all(refs.map((ref) => getDoc(ref)));

        const dailyRows = snaps
          .map((snap) => {
            if (!snap.exists()) return null;

            const data = snap.data() || {};
            const rowDate = typeof data.date === "string" ? data.date : "";

            if (!rowDate || !allowedDateSet.has(rowDate)) return null;
            if (typeof data.kidId !== "string" || data.kidId !== resolvedKidId) {
              return null;
            }
            if (resolvedUserId && data.userId && data.userId !== resolvedUserId) {
              return null;
            }

            return {
              id: snap.id,
              date: rowDate,
              sessions: toNumber(data.sessions),
              completed: toNumber(data.completed),
              totalMs: toNumber(data.totalMs),
              updatedAt: data.updatedAt || null,
              userId: data.userId || "",
              kidId: data.kidId || "",
              levels: normalizeLevelsMap(data.levels),
            };
          })
          .filter(Boolean)
          .sort((a, b) => a.date.localeCompare(b.date));

        const dailySeries = dailyRows.map((row) => ({
          date: row.date,
          sessions: row.sessions,
          completed: row.completed,
          totalMs: row.totalMs,
        }));

        const levelsAgg = {};

        for (const row of dailyRows) {
          for (const [levelId, levelData] of Object.entries(row.levels)) {
            if (
              !levelData ||
              typeof levelData !== "object" ||
              Array.isArray(levelData)
            ) {
              continue;
            }

            if (!levelsAgg[levelId]) {
              levelsAgg[levelId] = {
                id: levelId,
                title: levelData.title || levelId,
                sessions: 0,
                completed: 0,
                totalMs: 0,
              };
            }

            levelsAgg[levelId].sessions += toNumber(levelData.sessions);
            levelsAgg[levelId].completed += toNumber(levelData.completed);
            levelsAgg[levelId].totalMs += toNumber(levelData.totalMs);

            if (levelData.title) {
              levelsAgg[levelId].title = levelData.title;
            }
          }
        }

        commit("setDaily", dailyRows);
        commit("setDailySeries", dailySeries);
        commit("setLevelsAgg", levelsAgg);
        commit("setLastFetch", Date.now());
      } catch (err) {
        console.error("[dashboardStats] fetch error", err);
        commit("setError", err?.message || String(err));
        commit("setDaily", []);
        commit("setDailySeries", []);
        commit("setLevelsAgg", {});
      } finally {
        commit("setLoading", false);
      }
    },
  },

  getters: {
    totalsN: (state) => (n) => {
      const safeN = Math.min(30, Math.max(1, Number(n) || 1));
      const src = state.dailySeries.slice(-safeN);

      const sessions = src.map((d) => toNumber(d.sessions)).reduce(sum, 0);
      const completed = src.map((d) => toNumber(d.completed)).reduce(sum, 0);
      const totalMs = src.map((d) => toNumber(d.totalMs)).reduce(sum, 0);

      return { sessions, completed, totalMs };
    },

    totalsToday(state) {
      const today = isoLocalDate();
      const row = state.daily.find((item) => item.date === today);

      return {
        sessions: toNumber(row?.sessions),
        completed: toNumber(row?.completed),
        totalMs: toNumber(row?.totalMs),
      };
    },

    totals7d(state, getters) {
      return getters.totalsN(7);
    },

    totals30d(state, getters) {
      return getters.totalsN(30);
    },

    seriesSessions(state) {
      return state.dailySeries.map((d) => ({
        x: d.date,
        y: toNumber(d.sessions),
      }));
    },

    topLevels30d(state) {
      const arr = Object.values(state.levelsAgg || {});

      arr.sort((a, b) => {
        if (b.sessions !== a.sessions) return b.sessions - a.sessions;
        if (b.completed !== a.completed) return b.completed - a.completed;
        return b.totalMs - a.totalMs;
      });

      return arr;
    },
  },
};